import { segmentTrackUnauthorizedBusDetailPaneShown } from "analytics/analyticTrackEvents";
import DetailPaneMessageCard from "components/detailPane/shared/DetailPaneMessageCard";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import React, { useEffect } from "react";
import { ScopedBus } from "types/busType";
import { RegionType } from "types/regionType";
import { canUnlockBuses } from "types/roleType";
import { maybeGetSubscriptionForRegion } from "types/subscriptionType";
import BusDetailPane from "./BusDetailPane";
import { getBusSizeTags } from "./busSizeTags";
import {
    BusUnlockFailureReason,
    deriveBusUnlockFailureReason,
    UNLOCK_FAILURE_OUT_OF_BUSES,
    UNLOCK_FAILURE_ROLE_CANNOT_UNLOCK,
    UNLOCK_FAILURE_UNKNOWN
} from "./busUnlockState";
import ConnectedLinesSection from "./ConnectedLinesSection";
// There is nothing to link an account manager to -- no AM on the team record --
// so, like the In-Queue add-on upsells, we name who to talk to and stop there.
// This replaced a "Contact sales" button that opened a blank email with no bus
// or plan context: across ~7,000 locked panes it was clicked 10 times.
const CONTACT_ACCOUNT_MANAGER = "Contact your Account Manager";

type UnauthorizedDetailPaneProps = {
    readonly scopedBus: ScopedBus;
    /**
     * Set when the unlock *request* errored (network, auth, server fault)
     * rather than the server refusing it. There is no refusal to attribute in
     * that case -- the team's bus count and the user's role are both beside the
     * point -- so the pane falls back to the unexplained-failure copy.
     */
    readonly unlockRequestErrored?: boolean;
};

const UnauthorizedDetailPane: React.FC<UnauthorizedDetailPaneProps> = (
    props: UnauthorizedDetailPaneProps
) => {
    const { scopedBus, unlockRequestErrored = false } = props;
    const { bus, scopedCapacityEnergyCost } = scopedBus;
    const { generator, componentConfig } = useMapViewRoutingMetadata();
    const user = useUserDataContext();

    // The unlock didn't happen; work out which of the reasons applies so the
    // pane can explain the one that's actually true -- an errored request has
    // no attributable reason at all, so it takes the unexplained bucket.
    const unlockFailureReason = unlockRequestErrored
        ? UNLOCK_FAILURE_UNKNOWN
        : deriveBusUnlockFailureReason({
              canUnlock: canUnlockBuses(user.role),
              maybeSubscription: maybeGetSubscriptionForRegion(
                  user.subscriptions,
                  bus.region
              )
          });

    useEffect(() => {
        segmentTrackUnauthorizedBusDetailPaneShown(
            bus.niraBusId,
            unlockFailureReason
        );
    }, [bus.niraBusId, unlockFailureReason]);

    const { energyTag, chargingTag, capacityTag } = getBusSizeTags({
        scopedCapacityEnergyCost,
        componentConfig,
        generator
    });

    return (
        <div>
            <BusDetailPane
                energyTag={energyTag}
                chargingTag={chargingTag}
                capacityTag={capacityTag}
                constraintDetailsSections={[
                    getUnauthorizedConstraintDetailsSection(
                        bus.region,
                        unlockFailureReason
                    )
                ]}
                connectedLinesSection={<ConnectedLinesSection isLocked />}
                canAccessLocalStudies={false}
            />
        </div>
    );
};

type UnauthorizedCopy = {
    readonly heading: string;
    readonly description: string;
};

const getUnauthorizedCopy = (
    reason: BusUnlockFailureReason,
    region: RegionType
): UnauthorizedCopy => {
    if (reason === UNLOCK_FAILURE_ROLE_CANNOT_UNLOCK) {
        // An account manager can't widen a user's own permissions, so this one
        // points at the team's admins instead.
        return {
            heading: "Ask an admin to unlock this bus",
            description:
                "Your account has view-only access, so it can't unlock buses."
        };
    }

    if (reason === UNLOCK_FAILURE_OUT_OF_BUSES) {
        return {
            heading: "No buses left in subscription",
            description: `Your team has unlocked every bus in its ${region} subscription. ${CONTACT_ACCOUNT_MANAGER} to add more.`
        };
    }

    return {
        heading: "Couldn't unlock this bus",
        description: `Something went wrong unlocking this bus. Try selecting it again. ${CONTACT_ACCOUNT_MANAGER} if it keeps happening.`
    };
};

const getUnauthorizedConstraintDetailsSection = (
    region: RegionType,
    reason: BusUnlockFailureReason
): JSX.Element => {
    const { heading, description } = getUnauthorizedCopy(reason, region);

    return (
        <DetailPaneMessageCard heading={heading} description={description} />
    );
};

export default UnauthorizedDetailPane;
